import {inject, Injectable, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {delay, filter, take} from 'rxjs/operators';
import {AuthEvent, EventType, OAuthErrorType, SessionStorageService, ToastService, User, AuthService} from '@xmedical/core';
import {OAuthErrorService} from './oauth-error.service';
import {environment} from 'src/environments/environment';
import * as Sentry from '@sentry/angular';

interface UserData {
  name?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  sub?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements AuthService {
  private oidcService = inject(OidcSecurityService);
  private oauthErrorService = inject(OAuthErrorService);
  private toastService = inject(ToastService);
  private sessionStorage = inject(SessionStorageService);
  private router = inject(Router);

  events$: Observable<AuthEvent>;
  private eventsSubject = new Subject<AuthEvent>();
  private isAuthenticatedFlag = signal(false);
  private userData = signal<UserData | null>(null);
  private hasEmittedLogin = false;
  private refreshRetryCount = 0;
  private readonly maxRefreshRetries = 3;
  private retrySubscription: Subscription;
  private oauthErrorRetryCount = 0;
  private readonly maxOAuthErrorRetries = 3;
  private authReadyResolve: () => void;
  readonly authReady: Promise<void> = new Promise(resolve => this.authReadyResolve = resolve);

  constructor() {
    this.events$ = this.eventsSubject.asObservable();
    this.isAuthenticatedFlag.set(this.sessionStorage.getItem('user_authenticated', false));

    // Strip OIDC callback params from the URL after the first navigation completes.
    // Must be set up here (before checkAuth/authReady) to guarantee the subscription
    // exists before NavigationEnd fires.
    this.cleanOidcCallbackParams();

    // Check for OAuth error parameters in URL before setting up auth
    this.checkForOAuthErrors();

    this.setupAuthentication();
  }

  triggerEvent(event: AuthEvent): void {
    this.eventsSubject.next(event);
  }

  connectivityChanged(online: boolean): void {
    if (online && this.isAuthenticated()) {
      this.refreshToken();
    }
  }

  appFocusChanged(eventType: EventType): void {
    if (eventType === 'visible' && this.isAuthenticated()) {
      this.oidcService.checkAuth().subscribe();
    }
  }

  getAccountUrl(): string {
    return environment.authServer + '/account';
  }

  getFullName(): string {
    return this.userData()?.name ?? null;
  }

  getEmailAddress(): string {
    return this.userData()?.email ?? null;
  }

  getFirstName(): string {
    return this.getUserName();
  }

  getUserName(): string {
    return this.userData()?.given_name ?? null;
  }

  getSurname(): string {
    return this.userData()?.family_name ?? null;
  }

  getUserId(): string | null {
    return this.userData()?.sub ?? null;
  }

  getUser(): User {
    return {
      email: this.getEmailAddress(),
      firstName: this.getUserName(),
      surname: this.getSurname(),
      userId: this.getUserId(),
      name: this.getFullName()
    };
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedFlag();
  }

  login(): void {
    this.oidcService.authorize();
  }

  logout(): void {
    this.oidcService.logoff().subscribe();
  }

  refreshToken(): void {
    this.oidcService.forceRefreshSession().subscribe({
      next: () => {
        this.refreshRetryCount = 0;
      },
      error: (error) => {
        console.error('Token refresh failed:', error);
        this.scheduleRefreshRetry();
      }
    });
  }

  private setupAuthentication(): void {
    // Combine both streams so login event only fires when user data is available
    combineLatest([
      this.oidcService.isAuthenticated$,
      this.oidcService.userData$
    ]).subscribe(([{isAuthenticated}, {userData}]) => {
      const wasAuthenticated = this.isAuthenticatedFlag();
      this.isAuthenticatedFlag.set(isAuthenticated);
      this.sessionStorage.setItem('user_authenticated', isAuthenticated);
      this.userData.set(userData as UserData);

      if (isAuthenticated && userData && !this.hasEmittedLogin) {
        this.hasEmittedLogin = true;
        this.refreshRetryCount = 0;
        // Attribute Sentry events to this user (id only — no PII) so issues are
        // no longer anonymous (SWIFTCLAIM-6 had only geo/url to go on).
        Sentry.setUser({id: (userData as UserData).sub});
        this.eventsSubject.next(new AuthEvent('login'));
      } else if (wasAuthenticated && !isAuthenticated) {
        this.hasEmittedLogin = false;
        this.refreshRetryCount = 0;
        this.stopRetryTimer();
        Sentry.setUser(null);
        this.eventsSubject.next(new AuthEvent('logout'));
      }
    });

    this.oidcService.checkAuth().subscribe({
      next: ({isAuthenticated}) => {
        this.authReadyResolve();
        if (!isAuthenticated) {
          this.login();
        }
      },
      error: (error) => {
        console.error('Auth check failed', error);
        this.authReadyResolve();
        this.recoverFromAuthError();
      }
    });
  }

  private scheduleRefreshRetry(): void {
    if (this.refreshRetryCount >= this.maxRefreshRetries) {
      console.error('Max token refresh retries exceeded - logging out');
      this.logout();
      return;
    }

    this.refreshRetryCount++;
    // Exponential backoff: 2s, 4s, 8s
    const retryDelay = Math.pow(2, this.refreshRetryCount) * 1000;

    this.stopRetryTimer();
    this.retrySubscription = of(true).pipe(delay(retryDelay)).subscribe(() => {
      this.refreshToken();
    });
  }

  private stopRetryTimer(): void {
    if (this.retrySubscription) {
      this.retrySubscription.unsubscribe();
    }
  }

  private cleanOidcCallbackParams(): void {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (params.has('code') || params.has('state') || params.has('session_state') || params.has('iss')) {
      params.delete('code');
      params.delete('state');
      params.delete('session_state');
      params.delete('iss');

      const cleanUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
      // Wait for the Router's initial navigation to complete (including lazy module loading
      // and guard resolution), then navigate to the clean URL to strip OIDC callback params.
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        take(1)
      ).subscribe(() => {
        this.router.navigateByUrl(cleanUrl, {replaceUrl: true});
      });
    }
  }

  private recoverFromAuthError(): void {
    // Clean stale OIDC callback params from the URL (e.g. from a previous auth library session).
    // Without this, checkAuth() fails repeatedly on state mismatch and the user is stuck.
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const hadCallbackParams = params.has('code') || params.has('state');

    params.delete('code');
    params.delete('state');
    params.delete('session_state');

    if (hadCallbackParams) {
      const cleanUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
      this.router.navigateByUrl(cleanUrl, {replaceUrl: true});
      this.login();
    }
  }

  private checkForOAuthErrors(): void {
    const errorParams = this.oauthErrorService.extractErrorFromUrl();

    if (!errorParams) {
      return;
    }

    this.oauthErrorService.logError(errorParams);

    const errorType = this.oauthErrorService.getErrorType(errorParams.error);
    const userMessage = this.oauthErrorService.getUserFriendlyMessage(errorType, errorParams.error_description);

    this.eventsSubject.next(new AuthEvent('oauth_error', {errorParams, errorType}));

    this.handleOAuthError(errorType, userMessage, errorParams);

    this.oauthErrorService.cleanUrlParameters();
  }

  private handleOAuthError(errorType: OAuthErrorType, userMessage: string, _errorParams: any): void {
    switch (errorType) {
      case OAuthErrorType.TEMPORARILY_UNAVAILABLE:
      case OAuthErrorType.SERVER_ERROR:
        this.handleRetriableOAuthError(userMessage);
        break;

      case OAuthErrorType.AUTHENTICATION_EXPIRED:
        this.toastService.show({
          message: userMessage,
          options: {delay: 5000, classname: 'bg-warning'}
        });
        setTimeout(() => {
          this.login();
        }, 1000);
        break;

      case OAuthErrorType.ACCESS_DENIED:
        this.toastService.show({
          message: userMessage,
          options: {delay: 10000, classname: 'bg-warning'}
        });
        break;

      case OAuthErrorType.INVALID_REQUEST:
      case OAuthErrorType.INVALID_SCOPE:
      case OAuthErrorType.UNKNOWN:
      default:
        this.toastService.show({
          message: userMessage,
          options: {delay: 10000, classname: 'bg-danger'}
        });
        break;
    }
  }

  private handleRetriableOAuthError(userMessage: string): void {
    if (this.oauthErrorRetryCount >= this.maxOAuthErrorRetries) {
      this.toastService.show({
        message: 'Unable to connect to authentication service after multiple attempts. Please try again later.',
        options: {delay: 10000, classname: 'bg-danger'}
      });
      this.oauthErrorRetryCount = 0;
      return;
    }

    this.oauthErrorRetryCount++;
    const retryDelay = Math.pow(2, this.oauthErrorRetryCount) * 1000;

    this.toastService.show({
      message: `${userMessage} Retrying in ${retryDelay / 1000} seconds... (Attempt ${this.oauthErrorRetryCount}/${this.maxOAuthErrorRetries})`,
      options: {delay: retryDelay, classname: 'bg-info'}
    });

    setTimeout(() => {
      console.log(`Retrying OAuth login after error (attempt ${this.oauthErrorRetryCount}/${this.maxOAuthErrorRetries})`);
      this.login();
    }, retryDelay);
  }
}
